import { useState, useEffect } from 'react';
import { api } from './lib/api';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Credentials } from './pages/Credentials';
import { Users } from './pages/Users';
import { Tiers } from './pages/Tiers';
import { Layout } from './components/Layout';

type Page = 'dashboard' | 'credentials' | 'users' | 'tiers';

interface User {
  id: string;
  email: string;
  role: string;
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState<Page>('dashboard');

  useEffect(() => {
    const token = api.getToken();
    if (token) {
      api.getMe().then((result) => {
        if (result.success && result.data && result.data.role === 'admin') {
          setUser(result.data);
        } else {
          api.setToken(null);
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1) || 'dashboard';
      if (['dashboard', 'credentials', 'users', 'tiers'].includes(hash)) {
        setPage(hash as Page);
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleLogin = async (email: string, password: string) => {
    const result = await api.login(email, password);
    if (result.success && result.data) {
      if (result.data.user.role !== 'admin') {
        return { success: false, error: 'Access denied. Admin role required.' };
      }
      api.setToken(result.data.token);
      setUser(result.data.user);
      return { success: true };
    }
    return { success: false, error: result.error?.message || 'Login failed' };
  };

  const handleLogout = () => {
    api.setToken(null);
    setUser(null);
  };

  const navigate = (p: Page) => {
    window.location.hash = p;
    setPage(p);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <Layout currentPage={page} onNavigate={navigate} user={user} onLogout={handleLogout}>
      {page === 'dashboard' && <Dashboard />}
      {page === 'credentials' && <Credentials />}
      {page === 'users' && <Users />}
      {page === 'tiers' && <Tiers />}
    </Layout>
  );
}
